import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { apiGet } from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft, 
  Download,
  Printer,
  Users,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  FileText,
  Building2,
  Globe,
  Wallet,
  CreditCard,
  Receipt,
  PieChart,
  BarChart3,
  LineChart,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  Filter,
  Search,
  RefreshCw,
  Eye,
  ChevronDown,
  ChevronUp,
  Plane,
  UserCheck,
  UserX,
  Banknote,
  CircleDollarSign,
  Target,
  Award,
  Zap,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.4 } }
};

// Format currency
const formatCurrency = (amount, currency = 'AED') => {
  if (amount === undefined || amount === null) return `0 ${currency}`;
  return `${amount.toLocaleString()} ${currency}`;
};

// Format percentage
const formatPercent = (value) => {
  if (value === undefined || value === null) return '0%';
  return `${Math.round(value)}%`;
};

// Get status badge config
const getStatusConfig = (status) => {
  switch (status?.toLowerCase()) {
    case 'arrived':
      return { color: 'bg-emerald-500', text: 'Arrived', icon: CheckCircle };
    case 'abroad':
      return { color: 'bg-blue-500', text: 'Abroad', icon: Plane };
    case 'settled':
      return { color: 'bg-purple-500', text: 'Settled', icon: Wallet };
    case 'runaway':
      return { color: 'bg-red-500', text: 'Runaway', icon: UserX };
    case 'noarrival':
      return { color: 'bg-amber-500', text: 'No Arrival', icon: AlertTriangle };
    default:
      return { color: 'bg-slate-500', text: status || 'Unknown', icon: Clock };
  }
};

// Animated Counter Component
const AnimatedCounter = ({ value, prefix = '', suffix = '', duration = 1000 }) => {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    let start = 0;
    const end = typeof value === 'number' ? value : 0;
    const increment = end / (duration / 16);
    
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setDisplayValue(end);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, 16);
    
    return () => clearInterval(timer);
  }, [value, duration]);
  
  return <span>{prefix}{displayValue.toLocaleString()}{suffix}</span>;
};

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'blue', large = false }) => {
  const colorClasses = {
    blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30',
    emerald: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30',
    amber: 'from-amber-500/20 to-amber-600/10 border-amber-500/30',
    red: 'from-red-500/20 to-red-600/10 border-red-500/30',
    purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30',
    cyan: 'from-cyan-500/20 to-cyan-600/10 border-cyan-500/30',
  };
  
  const iconColors = {
    blue: 'text-blue-400 bg-blue-500/20',
    emerald: 'text-emerald-400 bg-emerald-500/20',
    amber: 'text-amber-400 bg-amber-500/20',
    red: 'text-red-400 bg-red-500/20',
    purple: 'text-purple-400 bg-purple-500/20',
    cyan: 'text-cyan-400 bg-cyan-500/20',
  };
  
  return (
    <motion.div variants={itemVariants}>
      <Card className={`bg-gradient-to-br ${colorClasses[color]} border-2 hover:scale-[1.02] transition-transform duration-300`}>
        <CardContent className={`${large ? 'p-6' : 'p-4'}`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-white/60 text-xs font-medium uppercase tracking-wider">{title}</p>
              <h3 className={`${large ? 'text-3xl' : 'text-2xl'} font-bold text-white mt-1`}>{value}</h3>
              {subtitle && <p className="text-white/50 text-sm mt-1">{subtitle}</p>}
              {trend && (
                <div className={`flex items-center gap-1 mt-2 ${trend === 'up' ? 'text-emerald-400' : 'text-red-400'}`}>
                  {trend === 'up' ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                  <span className="text-sm font-medium">{trendValue}</span>
                </div>
              )}
            </div>
            <div className={`p-3 rounded-xl ${iconColors[color]}`}>
              <Icon className="h-6 w-6" />
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Progress Ring Component
const ProgressRing = ({ progress, size = 120, strokeWidth = 10, color = '#10B981' }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-1000"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-bold text-white">{Math.round(progress)}%</span>
      </div>
    </div>
  );
};

// Supplier Detail Row Component
const SupplierDetailRow = ({ supplier, data, expanded, onToggle, currency = 'AED' }) => {
  const StatusIcon = getStatusConfig(data.mostCommonStatus).icon;
  
  return (
    <motion.div
      variants={itemVariants}
      className="bg-gradient-to-r from-slate-800/50 to-slate-900/50 rounded-2xl overflow-hidden border border-white/10 hover:border-white/20 transition-all"
    >
      {/* Main Row */}
      <div 
        className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Supplier Avatar */}
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
              {supplier.name?.charAt(0) || 'S'}
            </div>
            
            <div>
              <h3 className="font-bold text-white text-lg">{supplier.name}</h3>
              <div className="flex items-center gap-3 text-sm text-white/60">
                <span className="flex items-center gap-1">
                  <Globe className="h-3 w-3" />
                  {supplier.country || 'Unknown'}
                </span>
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {data.totalWorkers} workers
                </span>
              </div>
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="hidden md:flex items-center gap-6">
            <div className="text-right">
              <p className="text-white/50 text-xs">Total Cost</p>
              <p className="text-white font-bold">{formatCurrency(data.totals.cost, currency)}</p>
            </div>
            <div className="text-right">
              <p className="text-white/50 text-xs">Paid</p>
              <p className="text-emerald-400 font-bold">{formatCurrency(data.totals.paid, currency)}</p>
            </div>
            <div className="text-right">
              <p className="text-white/50 text-xs">Remaining</p>
              <p className="text-amber-400 font-bold">{formatCurrency(data.totals.remaining, currency)}</p>
            </div>
            <div className="text-right">
              <p className="text-white/50 text-xs">Collection</p>
              <p className={`font-bold ${data.collectionRate >= 80 ? 'text-emerald-400' : data.collectionRate >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                {formatPercent(data.collectionRate)}
              </p>
            </div>
          </div>
          
          {/* Expand Button */}
          <Button variant="ghost" size="icon" className="text-white/60 hover:text-white">
            {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </Button>
        </div>
        
        {/* Mobile Stats */}
        <div className="md:hidden grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-white/10">
          <div>
            <p className="text-white/50 text-xs">Total Cost</p>
            <p className="text-white font-bold">{formatCurrency(data.totals.cost, currency)}</p>
          </div>
          <div>
            <p className="text-white/50 text-xs">Collection Rate</p>
            <p className={`font-bold ${data.collectionRate >= 80 ? 'text-emerald-400' : 'text-amber-400'}`}>
              {formatPercent(data.collectionRate)}
            </p>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex justify-between text-xs text-white/50 mb-1">
            <span>Collection Progress</span>
            <span>{formatPercent(data.collectionRate)}</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <motion.div 
              className={`h-full rounded-full ${data.collectionRate >= 80 ? 'bg-emerald-500' : data.collectionRate >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
              initial={{ width: 0 }}
              animate={{ width: `${data.collectionRate}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </div>
        </div>
      </div>
      
      {/* Expanded Details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="p-4 pt-0 border-t border-white/10">
              {/* Detailed Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-white/50 text-xs">Total Workers</p>
                  <p className="text-white text-xl font-bold">{data.totalWorkers}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-white/50 text-xs">Arrived</p>
                  <p className="text-emerald-400 text-xl font-bold">{data.byStatus.arrived || 0}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-white/50 text-xs">Abroad</p>
                  <p className="text-blue-400 text-xl font-bold">{data.byStatus.abroad || 0}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-white/50 text-xs">Settled</p>
                  <p className="text-purple-400 text-xl font-bold">{data.byStatus.settled || 0}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-white/50 text-xs">Runaway</p>
                  <p className="text-red-400 text-xl font-bold">{data.byStatus.runaway || 0}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-white/50 text-xs">Deductions</p>
                  <p className="text-amber-400 text-xl font-bold">{formatCurrency(data.totals.deductions, 'USD')}</p>
                </div>
              </div>
              
              {/* Workers Table */}
              <div className="bg-white/5 rounded-xl overflow-hidden">
                <div className="p-3 border-b border-white/10">
                  <h4 className="font-semibold text-white flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Workers Details ({data.cases.length})
                  </h4>
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-white/5 sticky top-0">
                      <tr>
                        <th className="text-left p-3 text-white/60 text-xs font-medium">Worker</th>
                        <th className="text-left p-3 text-white/60 text-xs font-medium">Nationality</th>
                        <th className="text-left p-3 text-white/60 text-xs font-medium">Status</th>
                        <th className="text-right p-3 text-white/60 text-xs font-medium">Total</th>
                        <th className="text-right p-3 text-white/60 text-xs font-medium">Paid</th>
                        <th className="text-right p-3 text-white/60 text-xs font-medium">Remaining</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.cases.map((c, idx) => {
                        const statusConfig = getStatusConfig(c.arrivalStatus);
                        return (
                          <tr key={c.id || idx} className="border-b border-white/5 hover:bg-white/5">
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center text-white text-xs font-bold">
                                  {c.workerName?.charAt(0) || 'W'}
                                </div>
                                <span className="text-white text-sm font-medium">{c.workerName || 'Unknown'}</span>
                              </div>
                            </td>
                            <td className="p-3 text-white/70 text-sm">{c.nationality || 'N/A'}</td>
                            <td className="p-3">
                              <Badge className={`${statusConfig.color} text-white text-xs`}>
                                {statusConfig.text}
                              </Badge>
                            </td>
                            <td className="p-3 text-right text-white text-sm font-medium">
                              {formatCurrency(c.totalCost, currency)}
                            </td>
                            <td className="p-3 text-right text-emerald-400 text-sm font-medium">
                              {formatCurrency(c.paidAmount, currency)}
                            </td>
                            <td className="p-3 text-right text-amber-400 text-sm font-medium">
                              {formatCurrency(c.remainingAmount, currency)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Main Component
const SupplierReportsPage = () => {
  const navigate = useNavigate();
  const { suppliers, cases, fetchSuppliers, fetchCases, loading } = useApp();
  
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSuppliers, setExpandedSuppliers] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchSuppliers();
    fetchCases();
  }, [fetchSuppliers, fetchCases]);

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchSuppliers(), fetchCases()]);
    setRefreshing(false);
    toast.success('Data refreshed successfully');
  };

  // Calculate report data
  const reportData = useMemo(() => {
    // Return empty state if no data yet
    if (!cases || !cases.length) {
      return {
        filteredCases: [],
        activeCases: [],
        totalCost: 0,
        totalPaid: 0,
        totalDeductions: 0,
        totalRemaining: 0,
        supplierGroups: {},
        statusCounts: { arrived: 0, abroad: 0, settled: 0, runaway: 0, noarrival: 0 },
        collectionRate: 0,
        totalWorkers: 0,
        totalSuppliers: 0
      };
    }

    let filteredCases = [...cases];

    // Filter by supplier (using supplierId - camelCase from API)
    if (selectedSupplier !== 'all') {
      filteredCases = filteredCases.filter(c => c.supplierId === selectedSupplier);
    }

    // Filter by status (using arrivalStatus or status)
    if (selectedStatus !== 'all') {
      filteredCases = filteredCases.filter(c => {
        const status = c.arrivalStatus?.toLowerCase() || c.status?.toLowerCase();
        return status === selectedStatus;
      });
    }

    // Filter by month/year (using createdAt - camelCase from API)
    if (selectedMonth !== 'all' || selectedYear !== 'all') {
      filteredCases = filteredCases.filter(c => {
        const dateStr = c.createdAt || c.created_at;
        if (!dateStr) return true; // Include cases without date
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return true; // Include invalid dates
        const monthMatch = selectedMonth === 'all' || date.getMonth() + 1 === parseInt(selectedMonth);
        const yearMatch = selectedYear === 'all' || date.getFullYear() === parseInt(selectedYear);
        return monthMatch && yearMatch;
      });
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredCases = filteredCases.filter(c => 
        c.workerName?.toLowerCase().includes(query) ||
        c.passportNumber?.toLowerCase().includes(query) ||
        c.nationality?.toLowerCase().includes(query)
      );
    }

    // Calculate totals (excluding settled workers from financial calculations)
    const getStatusForFilter = (c) => (c.arrivalStatus || c.status || '').toLowerCase();
    const activeCases = filteredCases.filter(c => {
      const s = getStatusForFilter(c);
      return s !== 'settled' && s !== 'completed';
    });
    
    const totalCost = activeCases.reduce((sum, c) => sum + (c.totalCost || 0), 0);
    const totalPaid = activeCases.reduce((sum, c) => sum + (c.paidAmount || 0), 0);
    const totalDeductions = activeCases.reduce((sum, c) => sum + (c.deductionsUSD || 0), 0);
    const totalRemaining = activeCases.reduce((sum, c) => sum + (c.remainingAmount || 0), 0);

    // Status counts (check both arrivalStatus and status fields)
    const getStatus = (c) => (c.arrivalStatus || c.status || '').toLowerCase();
    const statusCounts = {
      arrived: filteredCases.filter(c => getStatus(c) === 'arrived').length,
      abroad: filteredCases.filter(c => getStatus(c) === 'abroad').length,
      settled: filteredCases.filter(c => getStatus(c) === 'settled' || getStatus(c) === 'completed').length,
      runaway: filteredCases.filter(c => getStatus(c) === 'runaway').length,
      noarrival: filteredCases.filter(c => getStatus(c) === 'noarrival').length,
    };

    // Group by supplier (using supplierId - camelCase from API)
    const supplierGroups = {};
    filteredCases.forEach(c => {
      const supplierId = c.supplierId || 'unknown';
      if (!supplierGroups[supplierId]) {
        const supplier = suppliers.find(s => s.id === supplierId);
        supplierGroups[supplierId] = {
          supplier: supplier || { name: 'Unknown', currency: 'AED', country: 'Unknown' },
          cases: [],
          totals: { cost: 0, paid: 0, deductions: 0, remaining: 0 },
          byStatus: {},
          totalWorkers: 0
        };
      }
      supplierGroups[supplierId].cases.push(c);
      supplierGroups[supplierId].totalWorkers++;
      
      // Status count per supplier
      const status = getStatus(c) || 'unknown';
      supplierGroups[supplierId].byStatus[status] = (supplierGroups[supplierId].byStatus[status] || 0) + 1;
      
      // Only count non-settled for totals
      const cStatus = getStatus(c);
      if (cStatus !== 'settled' && cStatus !== 'completed') {
        supplierGroups[supplierId].totals.cost += c.totalCost || 0;
        supplierGroups[supplierId].totals.paid += c.paidAmount || 0;
        supplierGroups[supplierId].totals.deductions += c.deductionsUSD || 0;
        supplierGroups[supplierId].totals.remaining += c.remainingAmount || 0;
      }
    });

    // Calculate collection rate per supplier
    Object.values(supplierGroups).forEach(group => {
      group.collectionRate = group.totals.cost > 0 
        ? (group.totals.paid / group.totals.cost) * 100 
        : 0;
      // Most common status
      const statusEntries = Object.entries(group.byStatus);
      group.mostCommonStatus = statusEntries.length > 0 
        ? statusEntries.sort((a, b) => b[1] - a[1])[0][0]
        : 'unknown';
    });

    return {
      filteredCases,
      activeCases,
      totalCost,
      totalPaid,
      totalDeductions,
      totalRemaining,
      supplierGroups,
      statusCounts,
      collectionRate: totalCost > 0 ? (totalPaid / totalCost) * 100 : 0,
      totalWorkers: filteredCases.length,
      totalSuppliers: Object.keys(supplierGroups).length
    };
  }, [cases, suppliers, selectedSupplier, selectedMonth, selectedYear, selectedStatus, searchQuery]);

  // Export to CSV
  const handleExportCSV = () => {
    if (!reportData?.filteredCases.length) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Worker Name', 'Nationality', 'Passport', 'Supplier', 'Status', 'Total Cost', 'Paid', 'Deductions', 'Remaining', 'Arrival Date', 'Created'];
    const rows = reportData.filteredCases.map(c => {
      const supplier = suppliers.find(s => s.id === c.supplierId);
      return [
        c.workerName || '',
        c.nationality || '',
        c.passportNumber || '',
        supplier?.name || 'Unknown',
        c.arrivalStatus || c.status || '',
        c.totalCost || 0,
        c.paidAmount || 0,
        c.deductionsUSD || 0,
        c.remainingAmount || 0,
        c.arrivalDate || '',
        c.createdAt ? new Date(c.createdAt).toLocaleDateString() : ''
      ];
    });

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `supplier_report_${selectedYear}_${selectedMonth !== 'all' ? selectedMonth : 'all'}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast.success('Report exported successfully');
  };

  // Export to Excel format (more detailed)
  const handleExportExcel = () => {
    if (!reportData?.filteredCases.length) {
      toast.error('No data to export');
      return;
    }

    // Create a more detailed report
    let content = 'SUPPLIER FINANCIAL REPORT\n';
    content += `Generated: ${new Date().toLocaleString()}\n`;
    content += `Period: ${selectedMonth !== 'all' ? months.find(m => m.value === selectedMonth)?.label : 'All Months'} ${selectedYear}\n\n`;
    
    content += 'SUMMARY\n';
    content += `Total Workers: ${reportData.totalWorkers}\n`;
    content += `Total Suppliers: ${reportData.totalSuppliers}\n`;
    content += `Total Cost: ${formatCurrency(reportData.totalCost, 'AED')}\n`;
    content += `Total Paid: ${formatCurrency(reportData.totalPaid, 'AED')}\n`;
    content += `Total Remaining: ${formatCurrency(reportData.totalRemaining, 'AED')}\n`;
    content += `Collection Rate: ${formatPercent(reportData.collectionRate)}\n\n`;
    
    content += 'BY STATUS\n';
    Object.entries(reportData.statusCounts).forEach(([status, count]) => {
      content += `${status}: ${count}\n`;
    });
    content += '\n';
    
    // Detailed data
    const headers = ['Worker Name', 'Nationality', 'Passport', 'Supplier', 'Status', 'Total Cost', 'Paid', 'Deductions', 'Remaining', 'Arrival Date'];
    content += headers.join('\t') + '\n';
    
    reportData.filteredCases.forEach(c => {
      const supplier = suppliers.find(s => s.id === c.supplierId);
      const row = [
        c.workerName || '',
        c.nationality || '',
        c.passportNumber || '',
        supplier?.name || 'Unknown',
        c.arrivalStatus || c.status || '',
        c.totalCost || 0,
        c.paidAmount || 0,
        c.deductionsUSD || 0,
        c.remainingAmount || 0,
        c.arrivalDate || ''
      ];
      content += row.join('\t') + '\n';
    });

    const blob = new Blob([content], { type: 'text/tab-separated-values;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `supplier_detailed_report_${new Date().toISOString().split('T')[0]}.xls`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast.success('Detailed report exported');
  };

  // Print report
  const handlePrint = () => {
    window.print();
  };

  // Toggle supplier expansion
  const toggleSupplierExpand = (supplierId) => {
    setExpandedSuppliers(prev => ({
      ...prev,
      [supplierId]: !prev[supplierId]
    }));
  };

  const isLoading = loading.suppliers || loading.cases;
  const months = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => (currentYear - i).toString());

  return (
    <div 
      className="space-y-6 pb-8" 
      data-testid="supplier-reports-page"
    >
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
            className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-white/10 hover:border-white/20"
          >
            <ArrowLeft className="h-5 w-5 text-white" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <BarChart3 className="h-8 w-8 text-blue-400" />
              Supplier Reports
            </h1>
            <p className="text-sm text-white/60">تقارير الموردين المالية / Comprehensive Financial Analytics</p>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleRefresh}
            variant="outline"
            disabled={refreshing}
            className="border-white/20 text-white hover:bg-white/10"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={handleExportCSV}
            variant="outline"
            className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
          >
            <Download className="h-4 w-4 mr-2" />
            CSV
          </Button>
          <Button
            onClick={handleExportExcel}
            variant="outline"
            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
          >
            <FileText className="h-4 w-4 mr-2" />
            Excel
          </Button>
          <Button
            onClick={handlePrint}
            variant="outline"
            className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Filters Card */}
      <div>
        <Card className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Filter className="h-5 w-5 text-blue-400" />
              Filters & Search / الفلاتر والبحث
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Search */}
              <div className="lg:col-span-2">
                <label className="text-xs text-white/60 mb-1 block">Search Worker</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input
                    placeholder="Name, passport, nationality..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-900/50 border-white/10 text-white placeholder:text-white/30"
                  />
                </div>
              </div>
              
              {/* Supplier */}
              <div>
                <label className="text-xs text-white/60 mb-1 block">Supplier</label>
                <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                  <SelectTrigger className="bg-slate-900/50 border-white/10 text-white">
                    <SelectValue placeholder="All Suppliers" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-white/10">
                    <SelectItem value="all" className="text-white">All Suppliers</SelectItem>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id} className="text-white">
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Month */}
              <div>
                <label className="text-xs text-white/60 mb-1 block">Month</label>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="bg-slate-900/50 border-white/10 text-white">
                    <SelectValue placeholder="All Months" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-white/10">
                    <SelectItem value="all" className="text-white">All Months</SelectItem>
                    {months.map(m => (
                      <SelectItem key={m.value} value={m.value} className="text-white">
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Year */}
              <div>
                <label className="text-xs text-white/60 mb-1 block">Year</label>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="bg-slate-900/50 border-white/10 text-white">
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-white/10">
                    <SelectItem value="all" className="text-white">All Years</SelectItem>
                    {years.map(y => (
                      <SelectItem key={y} value={y} className="text-white">
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Status Filter Pills */}
            <div className="mt-4 flex flex-wrap gap-2">
              <Badge 
                className={`cursor-pointer transition-all ${selectedStatus === 'all' ? 'bg-white text-slate-900' : 'bg-white/10 text-white hover:bg-white/20'}`}
                onClick={() => setSelectedStatus('all')}
              >
                All Status
              </Badge>
              {['arrived', 'abroad', 'settled', 'runaway', 'noarrival'].map(status => {
                const config = getStatusConfig(status);
                return (
                  <Badge 
                    key={status}
                    className={`cursor-pointer transition-all ${selectedStatus === status ? config.color + ' text-white' : 'bg-white/10 text-white hover:bg-white/20'}`}
                    onClick={() => setSelectedStatus(status)}
                  >
                    {config.text}
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-2xl bg-slate-800/50" />
          ))}
        </div>
      ) : reportData ? (
        <>
          {/* Main KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard
              title="Total Workers"
              value={<AnimatedCounter value={reportData.totalWorkers} />}
              subtitle={`${reportData.totalSuppliers} suppliers`}
              icon={Users}
              color="blue"
              large
            />
            <KPICard
              title="Total Cost"
              value={formatCurrency(reportData.totalCost, 'AED')}
              subtitle="Active workers only"
              icon={CircleDollarSign}
              color="cyan"
              large
            />
            <KPICard
              title="Total Paid"
              value={formatCurrency(reportData.totalPaid, 'AED')}
              subtitle={`${formatPercent(reportData.collectionRate)} collected`}
              icon={Wallet}
              color="emerald"
              trend="up"
              trendValue={formatPercent(reportData.collectionRate)}
              large
            />
            <KPICard
              title="Outstanding"
              value={formatCurrency(reportData.totalRemaining, 'AED')}
              subtitle="Remaining balance"
              icon={Receipt}
              color={reportData.totalRemaining > 0 ? 'amber' : 'emerald'}
              large
            />
          </div>

          {/* Secondary Stats & Collection Rate */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Collection Rate Ring */}
            <div>
              <Card className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-white/10 h-full">
                <CardContent className="p-6 flex flex-col items-center justify-center h-full">
                  <h3 className="text-white/60 text-sm font-medium mb-4">Collection Rate / نسبة التحصيل</h3>
                  <ProgressRing 
                    progress={reportData.collectionRate} 
                    size={150} 
                    strokeWidth={12}
                    color={reportData.collectionRate >= 80 ? '#10B981' : reportData.collectionRate >= 50 ? '#F59E0B' : '#EF4444'}
                  />
                  <div className="mt-4 text-center">
                    <p className="text-white/50 text-sm">
                      {formatCurrency(reportData.totalPaid, 'AED')} / {formatCurrency(reportData.totalCost, 'AED')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Status Distribution */}
            <div className="lg:col-span-2">
              <Card className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-white/10 h-full">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <PieChart className="h-5 w-5 text-purple-400" />
                    Status Distribution / توزيع الحالات
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {[
                      { key: 'arrived', label: 'Arrived', labelAr: 'وصل', color: 'emerald' },
                      { key: 'abroad', label: 'Abroad', labelAr: 'بالخارج', color: 'blue' },
                      { key: 'settled', label: 'Settled', labelAr: 'تم التسوية', color: 'purple' },
                      { key: 'runaway', label: 'Runaway', labelAr: 'هارب', color: 'red' },
                      { key: 'noarrival', label: 'No Arrival', labelAr: 'لم يصل', color: 'amber' },
                    ].map(item => (
                      <div 
                        key={item.key}
                        className={`p-4 rounded-xl bg-${item.color}-500/10 border border-${item.color}-500/30 text-center`}
                      >
                        <p className={`text-3xl font-bold text-${item.color}-400`}>
                          {reportData.statusCounts[item.key] || 0}
                        </p>
                        <p className="text-white/70 text-sm">{item.label}</p>
                        <p className="text-white/40 text-xs">{item.labelAr}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Deductions & Additional Info */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard
              title="Total Deductions"
              value={formatCurrency(reportData.totalDeductions, 'USD')}
              subtitle="الخصومات"
              icon={TrendingDown}
              color="red"
            />
            <KPICard
              title="Active Workers"
              value={<AnimatedCounter value={reportData.activeCases.length} />}
              subtitle="Non-settled"
              icon={UserCheck}
              color="emerald"
            />
            <KPICard
              title="Avg. Cost/Worker"
              value={formatCurrency(reportData.totalWorkers > 0 ? Math.round(reportData.totalCost / reportData.activeCases.length) : 0, 'AED')}
              subtitle="Per worker"
              icon={Target}
              color="purple"
            />
            <KPICard
              title="Avg. Remaining"
              value={formatCurrency(reportData.activeCases.length > 0 ? Math.round(reportData.totalRemaining / reportData.activeCases.length) : 0, 'AED')}
              subtitle="Per worker"
              icon={Banknote}
              color="amber"
            />
          </div>

          {/* Suppliers Detail Section */}
          <div>
            <Card className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-xl flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Building2 className="h-6 w-6 text-blue-400" />
                    Suppliers Detail / تفاصيل الموردين
                  </span>
                  <Badge className="bg-blue-500/20 text-blue-400">
                    {Object.keys(reportData.supplierGroups).length} Suppliers
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(reportData.supplierGroups).map(([supplierId, data]) => (
                  <SupplierDetailRow
                    key={supplierId}
                    supplier={data.supplier}
                    data={data}
                    currency={data.supplier.currency || 'AED'}
                    expanded={expandedSuppliers[supplierId]}
                    onToggle={() => toggleSupplierExpand(supplierId)}
                  />
                ))}
                
                {Object.keys(reportData.supplierGroups).length === 0 && (
                  <div className="text-center py-12">
                    <Building2 className="h-16 w-16 mx-auto text-white/20 mb-4" />
                    <p className="text-white/60">No suppliers found matching your criteria</p>
                    <p className="text-white/40 text-sm">Try adjusting your filters</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      ) : (
        <div>
          <Card className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-white/10">
            <CardContent className="py-20 text-center">
              <BarChart3 className="h-20 w-20 mx-auto text-white/20 mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">No Data Available</h3>
              <p className="text-white/60">Add some workers to see financial reports</p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SupplierReportsPage;
